import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Heart, Mail, Menu, Phone, X } from "lucide-react";
import { BkaWordmark } from "@/components/BkaLogo";
import { useSite } from "@/context/SiteContext";
import "@/pages/public/bka-vibrant-energy.css";

export function PublicHeader() {
  const { nav, s } = useSite();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const phone = (s("site.phone") || "").trim();
  const email = (s("site.email") || "").trim();

  const baseLinks = nav.length
    ? nav
    : [
        { label: "Home", href: "/" },
        { label: "About", href: "/about" },
        { label: "Kidney Health", href: "/kidney-health" },
        { label: "Our Efforts", href: "/our-efforts" },
        { label: "Support", href: "/get-support" },
        { label: "Blogs", href: "/blog" },
        { label: "Emerald Honours", href: "/emerald-honours" },
        { label: "Contact", href: "/contact" },
      ];

  // Team is a frontend-safe addition so the existing backend bootstrap does not need
  // to be redeployed just to expose the Board of Directors page.
  const links = baseLinks.some((l) => l.href === "/team")
    ? baseLinks
    : baseLinks.flatMap((l) =>
        l.href === "/about" ? [l, { label: "Team", href: "/team" }] : [l]
      );

  const testId = (label) => label.toLowerCase().replace(/[^a-z]+/g, "-");

  return (
    <header data-testid="public-header" className="sticky top-0 z-50 w-full">
      {/* utility band — stronger, bolder top band per the client revision */}
      <div data-testid="public-header-band" className="bg-bka-emDeep text-white">
        <div className="bka-container flex h-9 items-center justify-between gap-4 text-[11px] font-semibold">
          <p className="truncate tracking-[0.12em] text-white/70">
            A HEALTHIER PEOPLE. A STRONGER BAHAMAS.
          </p>
          <div className="hidden items-center gap-5 sm:flex">
            {phone && (
              <a
                href={`tel:${phone.replace(/[^\d+]/g, "")}`}
                data-testid="header-band-phone"
                className="inline-flex items-center gap-1.5 text-white/85 transition-colors hover:text-bka-orangeLight"
              >
                <Phone className="h-3 w-3" />
                {phone}
              </a>
            )}
            {email && (
              <a
                href={`mailto:${email}`}
                data-testid="header-band-email"
                className="inline-flex items-center gap-1.5 text-white/85 transition-colors hover:text-bka-orangeLight"
              >
                <Mail className="h-3 w-3" />
                {email}
              </a>
            )}
          </div>
        </div>
      </div>

      <div
        className={`w-full border-b bg-white/95 backdrop-blur-md transition-shadow duration-300 ${
          scrolled
            ? "border-bka-emerald/15 shadow-[0_10px_30px_-24px_rgba(13,51,38,0.5)]"
            : "border-bka-emerald/10"
        }`}
      >
        <div className="bka-container flex min-h-[104px] items-center justify-between gap-4 py-3">
          <Link to="/" data-testid="public-header-logo" aria-label="Bahamas Kidney Association home" className="mr-1 block shrink-0">
            <BkaWordmark />
          </Link>

          <nav className="hidden flex-1 items-center justify-end gap-1.5 xl:flex 2xl:gap-4" aria-label="Main navigation">
            {links.map((l) => {
              const emerald = l.href === "/emerald-honours";
              const feature = ["/about", "/our-efforts", "/get-involved", "/research"].includes(l.href);
              return (
                <NavLink
                  key={l.href}
                  to={l.href}
                  end={l.href === "/"}
                  data-testid={`public-nav-${testId(l.label)}`}
                  className={({ isActive }) =>
                    emerald
                      ? `inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3.5 py-2 text-[13px] font-extrabold uppercase tracking-[0.05em] transition-all duration-300 ${
                          isActive
                            ? "border-bka-emerald bg-bka-emerald text-white"
                            : "border-bka-emerald/35 bg-bka-aqua text-bka-emeraldDark hover:border-bka-emerald hover:bg-bka-emerald hover:text-white"
                        }`
                      : feature
                      ? `whitespace-nowrap rounded-full px-3 py-2 text-[13px] font-extrabold uppercase tracking-[0.05em] transition-all duration-300 ${
                          isActive
                            ? "bg-bka-emerald text-white"
                            : "text-bka-emeraldDark hover:bg-bka-orangeSoft hover:text-bka-orangeDark"
                        }`
                      : `bka-link-underline whitespace-nowrap text-[13px] font-bold uppercase tracking-[0.04em] transition-colors ${
                          isActive ? "text-bka-emerald" : "text-bka-navy bka-nav-energy"
                        }`
                  }
                >
                  {emerald && (
                    <span className="h-1.5 w-1.5 rounded-full bg-bka-orange" aria-hidden="true" />
                  )}
                  {l.label}
                </NavLink>
              );
            })}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <Link
              to="/donate"
              data-testid="public-nav-donate-button"
              className="bka-header-donate group inline-flex min-h-[44px] shrink-0 items-center gap-2 rounded-full px-4 py-2.5 text-xs font-extrabold uppercase tracking-[0.04em] text-white transition-all duration-300 sm:min-h-[46px] sm:px-5 sm:text-sm"
            >
              <Heart className="h-4 w-4 transition-transform duration-300 group-hover:scale-110" />
              Donate
            </Link>
            <button
              type="button"
              data-testid="public-mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              aria-label="Toggle navigation menu"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-bka-emerald/20 text-bka-navy xl:hidden"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <nav
          data-testid="public-mobile-menu"
          className="border-b border-bka-emerald/15 bg-white px-5 pb-6 pt-2 shadow-lg xl:hidden"
          aria-label="Mobile navigation"
        >
          {links.map((l) => (
            <NavLink
              key={l.href}
              to={l.href}
              end={l.href === "/"}
              data-testid={`public-mobile-nav-${testId(l.label)}`}
              className={({ isActive }) =>
                `flex min-h-[48px] items-center justify-between border-b border-slate-100 py-3.5 text-sm font-semibold ${
                  isActive
                    ? "text-bka-emerald"
                    : l.href === "/emerald-honours"
                      ? "text-bka-emeraldDark"
                      : "text-bka-navy"
                }`
              }
            >
              {l.label}
              {l.href === "/emerald-honours" && (
                <span className="h-1.5 w-1.5 rounded-full bg-bka-orange" aria-hidden="true" />
              )}
            </NavLink>
          ))}
        </nav>
      )}
    </header>
  );
}
